import { useState, useEffect, ChangeEvent } from "react";
import { Dispatch, SetStateAction } from "react";
import {
  Modal,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  CardMedia,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useUploadMediaMutation } from "../service/common/api";
import { PLATFORM_TYPE } from "../constants/enum";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isEdit?: boolean;
  editData?: {
    _id: string;
    content: { title: string; description: string; title_ar?: string; description_ar?: string };
    image: string;
    platformType?: number;
  } | null;
  onSubmit?: (title: string, description: string, image: string, platformType: number, titleAr: string, descriptionAr: string) => void;
  loading?: boolean;
}

export default function AddBanner({
  open,
  onClose,
  setOpen,
  isEdit = false,
  editData,
  onSubmit,
  loading = false,
}: ModalProps) {
  const [platformType, setPlatformType] = useState<number | "">("");
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionAr, setDescriptionAr] = useState("");
  const [image, setImage] = useState("");
  const [imageError, setImageError] = useState<string>("");
  const [errors, setErrors] = useState<{
    platformType?: string;
    title?: string;
    description?: string;
    image?: string;
  }>({});

  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  useEffect(() => {
    setErrors({});
    setImageError("");
    if (open && isEdit && editData) {
      setPlatformType(editData.platformType ?? "");
      setTitle(editData.content.title);
      setTitleAr(editData.content.title_ar || "");
      setDescription(editData.content.description);
      setDescriptionAr(editData.content.description_ar || "");
      setImage(editData.image);
    } else if (open && !isEdit) {
      setPlatformType("");
      setTitle("");
      setTitleAr("");
      setDescription("");
      setDescriptionAr("");
      setImage("");
    }
  }, [open, isEdit, editData]);

  const checkImageDimensions = (file: File): Promise<string> =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const { width, height } = img;
        const ratio = width / height;
        const targetRatio = 2 / 1;
        const tolerance = 0.07;
        if (Math.abs(ratio - targetRatio) > tolerance) {
          resolve(
            `Invalid aspect ratio detected (${width}×${height} px, ratio ${ratio.toFixed(2)}). ` +
            `Please upload an image with a 2:1 aspect ratio (recommended: 1440×716 px).`
          );
        } else {
          resolve("");
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve("");
      };
      img.src = url;
    });

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    const dimError = await checkImageDimensions(file);
    if (dimError) {
      setImageError(dimError);
      return;
    }
    setImageError("");

    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await uploadMedia(formData).unwrap();
      const imageUrl = res?.data?.url || res?.url || res?.data;
      if (imageUrl) {
        setImage(imageUrl);
        if (errors.image) setErrors((prev) => ({ ...prev, image: undefined }));
        toast.success("Image uploaded successfully");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Upload failed");
    }
  };

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!platformType) newErrors.platformType = "Platform type is required";
    if (!title.trim()) newErrors.title = "Title is required";
    if (!description.trim()) newErrors.description = "Description is required";
    if (!image) newErrors.image = "Image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (onSubmit) {
      onSubmit(title, description, image, platformType as number, titleAr, descriptionAr);
    }
  };

  return (
    <Modal
      className="modal addNotification_modal"
      open={open}
      onClose={() => {
        onClose();
        setErrors({});
        setImageError("");
      }}
    >
      <div className="modal-dialog" style={{ maxWidth: 700, width: "100%" }}>
        <div className="modal-body">
          <div className="modal_title">
            <h2 style={{ whiteSpace: "nowrap" }}>
              {isEdit ? "Edit Banner" : "Add New Banner"}
            </h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form">
            <div className="control_group">
              <label>Platform Type</label>
              <Select
                fullWidth
                displayEmpty
                value={platformType}
                onChange={(e) => {
                  setPlatformType(e.target.value as number);
                  if (errors.platformType)
                    setErrors((prev) => ({ ...prev, platformType: undefined }));
                }}
                error={!!errors.platformType}
              >
                <MenuItem value="" disabled>
                  Select Platform Type
                </MenuItem>
                {Object.keys(PLATFORM_TYPE)
                  .filter((k) => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(PLATFORM_TYPE as any)[key]}>
                      {key}
                    </MenuItem>
                  ))}
              </Select>
              {errors.platformType && (
                <h6 className="err_msg">{errors.platformType}</h6>
              )}
            </div>

            <div className="control_group">
              <label>Banner Image</label>
              <div
                className="upload_image no_radius v2 mb_10"
                style={{ width: "100%", height: "150px" }}
              >
                <label
                  htmlFor="banner-image-upload"
                  style={{
                    cursor: isUploading ? "not-allowed" : "pointer",
                    width: "100%",
                    height: "100%",
                    display: "block",
                  }}
                >
                  <input
                    id="banner-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    disabled={isUploading}
                    onChange={handleImageUpload}
                  />
                  <div
                    className="upload_image_holder"
                    style={{ width: "100%", height: "100%", position: "relative" }}
                  >
                    {isUploading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <CircularProgress size={24} />
                      </div>
                    ) : image ? (
                      <figure style={{ width: "100%", height: "100%", margin: 0 }}>
                        <CardMedia
                          component="img"
                          image={image}
                          style={{ height: "100%", width: "100%", objectFit: "contain" }}
                        />
                        <IconButton
                          className="remove_img"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setImage("");
                            setImageError("");
                          }}
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            background: "rgba(255,255,255,0.7)",
                            padding: "4px",
                          }}
                        >
                          <CloseIcon style={{ fontSize: "18px" }} />
                        </IconButton>
                      </figure>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <img
                          src="/static/images/placeholder.png"
                          alt=""
                          style={{ width: "40px", marginBottom: "10px" }}
                        />
                        <span>Click to upload banner image</span>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              <div style={{ marginTop: "8px", padding: "8px 12px", background: "#f5f7fa", borderRadius: "6px", border: "1px solid #e0e4ea" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#555", lineHeight: "1.6" }}>
                  <strong>Image Requirements:</strong><br />
                  Recommended resolution: <strong>1440 × 716 px</strong><br />
                  Required aspect ratio: <strong>2:1</strong> (width:height)<br />
                  <span style={{ color: "#888" }}>Images with a different aspect ratio will be rejected. Use dimensions like 1440×716, 1920×960, 1280×640, etc.</span>
                </p>
              </div>
              {imageError && (
                <h6 className="err_msg" style={{ marginTop: "6px" }}>{imageError}</h6>
              )}
              {errors.image && <h6 className="err_msg">{errors.image}</h6>}
            </div>

            {/* Title row */}
            <div style={{ display: "flex", gap: 16 }}>
              <div className="control_group" style={{ flex: 1 }}>
                <label>Title</label>
                <TextField
                  placeholder="Enter title"
                  fullWidth
                  hiddenLabel
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (errors.title)
                      setErrors((prev) => ({ ...prev, title: undefined }));
                  }}
                  error={!!errors.title}
                />
                {errors.title && <h6 className="err_msg">{errors.title}</h6>}
              </div>
              <div className="control_group" style={{ flex: 1 }}>
                <label>عنوان</label>
                <TextField
                  placeholder="أدخل العنوان"
                  fullWidth
                  hiddenLabel
                  value={titleAr}
                  onChange={(e) => setTitleAr(e.target.value)}
                />
              </div>
            </div>

            {/* Description row */}
            <div style={{ display: "flex", gap: 16 }}>
              <div className="control_group" style={{ flex: 1 }}>
                <label>Description</label>
                <TextField
                  placeholder="Enter description"
                  fullWidth
                  hiddenLabel
                  multiline
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description)
                      setErrors((prev) => ({ ...prev, description: undefined }));
                  }}
                  maxRows={5}
                  minRows={5}
                  error={!!errors.description}
                />
                {errors.description && (
                  <h6 className="err_msg">{errors.description}</h6>
                )}
              </div>
              <div className="control_group" style={{ flex: 1 }}>
                <label>وصف</label>
                <TextField
                  placeholder="أدخل الوصف"
                  fullWidth
                  hiddenLabel
                  multiline
                  value={descriptionAr}
                  onChange={(e) => setDescriptionAr(e.target.value)}
                  maxRows={5}
                  minRows={5}
                />
              </div>
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => {
                  setErrors({});
                  setOpen(false);
                }}
                disabled={loading || isUploading}
              >
                Cancel
              </Button>

              <Button
                className="btn btn_primary"
                disabled={loading || isUploading}
                onClick={handleSubmit}
              >
                {loading
                  ? isEdit
                    ? "Updating..."
                    : "Saving..."
                  : isEdit
                  ? "Update"
                  : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
